import TableRow from '@mui/material/TableRow';
import TableCell from '../../Utilities/TruncatedTableCell';
import { HeadCell } from '../../Utilities/SortableTableHead';
import MapIcon from '@mui/icons-material/Map';
import IconButton from '@mui/material/IconButton';
import { SiteListViewModel } from '../ViewModels/SiteListViewModel';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { useContext, useState } from 'react';
import { SessionContext } from '../../Contexts/SessionContext';
import { SurveyViewModel } from '../../Contexts/SessionState';
import { APIContext } from '../../Contexts/APIContext';
import { SiteNavigationType } from "../../Utilities/ViewModels/SiteNavigationType";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Popover } from '@mui/material';


interface IProps {
    row: SiteListViewModel;
    refresh: () => void;
    headCells: HeadCell<SiteListViewModel>[];
    setSiteId: (id: SiteListViewModel) => void;
    type: SiteNavigationType;
    setDeselectionOpen?: (id: string) => void;
    multiDeselect?: boolean;
}

export default function SiteRow(props: IProps) {
    const { row, headCells, setSiteId, type, setDeselectionOpen, multiDeselect } = props;
    const { setSessionField, sessionState, updateSessionState } = useContext(SessionContext);
    const { showMessage } = useContext(APIContext);
    const openInGoogleMaps = (e: any) => {
        e.stopPropagation();
        e.preventDefault();

        const url = `https://www.google.com/maps?q=${row.lat},${row.long}`;
        window.open(url, "_blank");
    };

    const site = sessionState.selectedSitesAndSurveys.find(x => x.siteRef === row.reference || x.surveyRef === row.reference); // this is covering older audits 

    const pendingDeselect = site?.sitePendingDeselect ?? false;
    const reason = site?.reasonForSiteRemoval;
    const checked = type === "siteFiltering" ?
        sessionState.landusePage.isAllSelected
            ? sessionState.landusePage.sites.find((x) => x === row.reference) === undefined
            : sessionState.landusePage.sites.find((x) => x === row.reference) !== undefined
        :
        site?.siteActive;

    const oncheckboxchange = (e: any) => {
        e.stopPropagation();
        if (checked && setDeselectionOpen) {
            setDeselectionOpen(row.reference);
            if (multiDeselect) {
                const updatedSites = sessionState.selectedSitesAndSurveys.map(site =>
                    site.siteRef === row.reference
                        ? { ...site, sitePendingDeselect: !site.sitePendingDeselect }
                        : site
                );

                updateSessionState({ selectedSitesAndSurveys: updatedSites });
            }
            return;
        }
        if (multiDeselect) {
            return;
        }

        if (type === 'siteFiltering') {

            var sites = sessionState.landusePage.sites;

            var siteExists = sites.find((site) => site === row.reference);

            if (siteExists) {
                const index = sites.indexOf(row.reference);
                if (index > -1) {
                    sites.splice(index, 1);
                }
            }
            else {
                sites.push(row.reference);
            }

            const otherSiteSurvey = sites.find((site) => (site === row.nextSurvey) || (site === row.lastSurvey));

            if (otherSiteSurvey) {
                const firstSiteRef = otherSiteSurvey < row.reference ? otherSiteSurvey : row.reference;
                const secondSiteRef = otherSiteSurvey < row.reference ? row.reference : otherSiteSurvey;
                showMessage("error", "Site: " + firstSiteRef + " has been re-surveyed by Site: " + secondSiteRef + " only one site can be selected");
                return;
            }

            setSessionField('landusePage.sites', sites);
            return;
        }

        const selectedSites = sessionState.selectedSitesAndSurveys;

        const currentSite = selectedSites.find((site) => (site.siteRef === row.reference))

        const otherSiteSurvey = selectedSites.find((site) => (site.resurveyRef === row.reference && site.siteActive) || (currentSite?.resurveyRef === site.surveyRef && site.siteActive));

        if (otherSiteSurvey) {
            const firstSiteRef = otherSiteSurvey.surveyRef < row.reference ? otherSiteSurvey.surveyRef : row.reference;
            const secondSiteRef = otherSiteSurvey.surveyRef < row.reference ? row.reference : otherSiteSurvey.surveyRef;
            showMessage("error", "Site: " + firstSiteRef + " has been re-surveyed by Site: " + secondSiteRef + " only one site can be selected");
            return;
        }

        let updatedSites: SurveyViewModel[];

        updatedSites = selectedSites.map((site) =>
            site.siteRef === row.reference
                ? { ...site, reasonForSurveyRemoval: "", reasonForSiteRemoval: '', siteActive: !site.siteActive, surveyActive: !site.surveyActive }
                : site

        );
        setSessionField('selectedSitesAndSurveys', updatedSites);
    }

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const formatNumber = (value: number | null | undefined) =>
        value?.toFixed(2).replace(/[.]00$/, "") ?? "";

    const renderCellContent = (property: string) => {
        switch (property) {
            case 'Reference':
                return <Typography noWrap>{row.reference.split('|')[0]}</Typography>;
            case 'Description':
                return <Typography noWrap>{row.description}</Typography>;
            case 'Descriptions':
                return row.descriptions?.filter(Boolean).length > 0 && (
                    <>
                        <IconButton size="small" onClick={(e) => {
                            e.stopPropagation();
                            handleOpen(e);
                        }}>
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}
                            PaperProps={{
                                sx: { p: 1, whiteSpace: 'pre-line', maxWidth: 300 }
                            }}
                        >
                            <Typography variant="body2">
                                {row.descriptions.filter(Boolean).join('\n')}
                            </Typography>
                        </Popover>
                    </>
                );
            case 'HasMap':
                return row.hasMap && (
                    <IconButton onClick={(e) => openInGoogleMaps(e)} size="small">
                        <MapIcon sx={{ m: -1, p: 0 }} />
                    </IconButton>
                );
            case 'Area':
                return <Typography noWrap>{row.area}</Typography>;
            case 'City':
                return <Typography noWrap>{row.city}</Typography>;
            case 'Location':
                return <Typography noWrap>{row.location.replace(/\s*\(.*?\)\s*/g, '').trim()}</Typography>;
            case 'SurveyType':
                return <Typography noWrap>{row.surveyType}</Typography>;
            case 'MostRecentSurveyDate':
                return <Typography noWrap>{row.mostRecentSurveyDate != null ? new Date(row.mostRecentSurveyDate?.toString())?.toLocaleDateString("en-GB") : ''}</Typography>;
            case 'DayOfWeekNo':
                return <Typography noWrap>{row.dayOfWeek}</Typography>;
            case 'Param1':
                return <Typography noWrap>{formatNumber(row.param1)}</Typography>;
            case 'Param2':
                return <Typography noWrap>{formatNumber(row.param2)}</Typography>;
            case 'Param3':
                return <Typography noWrap>{formatNumber(row.param3)}</Typography>;
            case 'Param4':
                return <Typography noWrap>{formatNumber(row.param4)}</Typography>;
            case 'Param5':
                return <Typography noWrap>{formatNumber(row.param5)}</Typography>;
            case 'Status':
                return <Typography>{row.status}</Typography>;
            case 'TravelPlan':
                return <Typography>{row.travelPlan}</Typography>;
            case 'LocnSubCategory':
                return <Typography>{row.locnSubCategory}</Typography>;
            case 'Population1Mile':
                return <Typography>{row.population1Mile}</Typography>;
            case 'Population5Miles':
                return <Typography>{row.population5Miles}</Typography>;
            case 'Population500m':
                return <Typography>{row.population500m}</Typography>;
            case 'CarOwner':
                return <Typography>{row.carOwner}</Typography>;
            case 'PtalRating':
                return <Typography>{row.ptalRating}</Typography>;
            default:
                return null;
        }
    };

    return (
        <TableRow
            onClick={() => setSiteId(row)}
            style={{ cursor: "pointer", backgroundColor: row.isCovid19 ? "rgb(192, 220, 192)" : row.isSAM ? "rgb(255, 215, 0)" : "" }}
        >
            {(type === 'summaryFiltering' || type === 'siteFiltering') &&
                <TableCell align="center" onClick={oncheckboxchange}>
                    <Checkbox checked={checked} style={{ padding: '0px' }} disabled={ multiDeselect} />
                    {(multiDeselect && checked)&&
                        <Checkbox checked={!pendingDeselect} style={{ padding: '0px' }} />
                    }
                </TableCell>
            }
            {headCells
                .filter(cell => cell.show)
                .map(cell => {
                    const property = cell.property ?? String(cell.id);
                    const content = renderCellContent(property);
                    return (
                        <TableCell key={property} align="center" sx={cell.sx}>
                            {content}
                        </TableCell>
                    );
                })}
            {type === 'summaryFiltering' &&
                <TableCell>{reason}</TableCell>
            }
            <TableCell sx={type === 'summaryFiltering'
                ? { width: '48px', minWidth: '48px', maxWidth: '48px' }
                : undefined} />
        </TableRow>
    );
}
